// LT-350 (LT-325 C3): first-visit operator onboarding banner on the new
// Campaigns landing page. Dismiss persists per user via localStorage.
//
// Why this exists: Campaigns are *back* — the platform spent a phase
// where operators ran Sequences directly (LT-179 redirected /campaigns
// → /sequences). LT-348 + LT-349 reintroduce Campaign as the
// operator-facing initiative wrapping a Sequence. Existing operators
// landing on /campaigns for the first time need a one-line orientation.
import React, { useEffect, useState } from 'react';
import { Megaphone, X } from 'lucide-react';

import { Button } from '../../ui';
import { useAuth } from '../../context/AuthContext';
import { useTour } from '../../context/TourContext';

const STORAGE_KEY_PREFIX = 'cold_caller_campaigns_onboarding_dismissed_';

function storageKey(userId: string | undefined): string {
  return `${STORAGE_KEY_PREFIX}${userId ?? 'anon'}`;
}

const CampaignOnboardingBanner: React.FC = () => {
  const { user } = useAuth();
  const { startTour } = useTour();
  const [dismissed, setDismissed] = useState(true); // start hidden until we read storage

  useEffect(() => {
    try {
      const flag = localStorage.getItem(storageKey(user?.id));
      setDismissed(flag === 'true');
    } catch {
      // localStorage may be unavailable (private mode / SSR-style envs);
      // fall back to keeping the banner hidden rather than spamming.
      setDismissed(true);
    }
  }, [user?.id]);

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey(user?.id), 'true');
    } catch {
      // Same fallback — best-effort.
    }
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      role="region"
      aria-label="Campaigns onboarding"
      className="mb-4 px-4 py-3 rounded-ds-xl border border-accent-border bg-accent-tint flex items-start gap-3"
    >
      <div className="p-2 rounded-ds-md bg-accent border border-accent-border text-white">
        <Megaphone size={16} />
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-slate-50">
          Get started with Campaigns
        </h3>
        <p className="text-xs text-slate-300 mt-1 max-w-2xl">
          A Campaign is your outbound initiative — it carries the audience,
          owner, status lifecycle, goals + KPIs, and a retry strategy, placing
          the calls and dispatching follow-ups automatically. Take the tour to
          see how it all fits together, or dismiss this once you're oriented.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button intent="secondary" size="sm" onClick={() => startTour()}>
          Take the tour
        </Button>
        <button
          type="button"
          aria-label="Dismiss onboarding banner"
          onClick={dismiss}
          className="p-1 rounded-ds-md text-slate-400 hover:text-slate-200 hover:bg-white/5"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default CampaignOnboardingBanner;
